/** Recorded for a step the new answer branches away from, so the reviewer can see what is lost */
export const DCR_SKIPPED_BY_BRANCHING = 'Skipped by branching';
const isEmptyDcrValue = (value) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
/**
 * The connector renders a missing answer as prose rather than leaving it empty, and reports the
 * label of an unanswered step as the string "null". Both mean the step holds no answer.
 */
const NO_ANSWER_TEXT = ['null', 'undefined', 'no response', 'no text entered', 'no value'];
const isMissingAnswer = (value) => isEmptyDcrValue(value) ||
    (typeof value === 'string' && NO_ANSWER_TEXT.includes(value.trim().toLowerCase())) ||
    (Array.isArray(value) && value.every((item) => isMissingAnswer(item)));
/** Screen titles are prefixed with the step order, e.g. "2. New bleed - bleed location" */
const screenOrder = (step) => parseInt(String(step.screen).split('.')[0], 10) || 0;
const inScreenOrder = (steps) => [...steps].sort((left, right) => screenOrder(left) - screenOrder(right));
/** A step the participant never reached has no answer, so the branch it sits on was not taken */
const wasAnswered = (step) => !step.skipped && !isMissingAnswer(step.originalValue);
/** Whether the step holds a recorded response a change could strand */
export const branchingStepHadAnswer = (step) => wasAnswered(step);
/**
 * The difference between the path the participant took and the path the proposal takes. Only
 * stranded screens that actually hold a response are listed, and only newly reached screens are
 * asked for an answer: screens the change leaves alone are not part of the impact.
 */
export const dcrBranchingImpact = (steps, path, recordedPathStepIds, pathStepIds) => ({
    unreachableSteps: steps.filter((step) => recordedPathStepIds.has(step.stepId) && !pathStepIds.has(step.stepId) && branchingStepHadAnswer(step)),
    newlyReachableSteps: path.filter((step) => !recordedPathStepIds.has(step.stepId)),
});
const asArray = (value) => (Array.isArray(value) ? value : [value]);
/** Pre-fills each step with the answer already on record, so only the intended change is a change */
export const initialBranchingSelections = (steps) => steps.reduce((selections, step) => {
    if (!wasAnswered(step)) {
        return selections;
    }
    const hasOptions = step.desiredValue.options?.length > 0;
    if (!hasOptions) {
        selections[step.stepId] = step.originalValue;
    }
    else if (step.desiredValue.allowMultiples) {
        selections[step.stepId] = asArray(step.originalValue);
    }
    else {
        selections[step.stepId] = asArray(step.originalValue)[0];
    }
    return selections;
}, {});
const toLabel = (value, options = []) => {
    if (isMissingAnswer(value)) {
        return DCR_SKIPPED_BY_BRANCHING;
    }
    if (options.length === 0) {
        return asArray(value).map(String).join(', ');
    }
    return asArray(value)
        .map((item) => options.find((option) => String(option.value) === String(item))?.label ?? String(item))
        .join(', ');
};
/**
 * The answer on record for a step, worded as the participant saw it. The connector sends both a
 * display label and the raw answer, and only the raw answer is present for free text steps.
 */
export const branchingOriginalAnswer = (step) => {
    const options = step.desiredValue.options ?? [];
    return isMissingAnswer(step.originalLabel)
        ? toLabel(step.originalValue, options)
        : toLabel(step.originalLabel, options);
};
/** Steps on the path still need an answer before the request describes a complete activity */
export const findUnansweredBranchingSteps = (path, selections) => path.filter((step) => step.desiredValue.editable && isEmptyDcrValue(selections[step.stepId]));
/**
 * The change set for a branching activity: every reachable step, answered or stranded, so the
 * reviewer and the executing service both see the whole activity rather than one screen of it.
 *
 * Each row keeps the question as the reviewer read it when raising the request, next to the screen
 * code and the ids the change is executed against. The questions are worth storing because they
 * can quote earlier answers, so they cannot be reconstructed from the activity alone once the
 * answers have moved on.
 */
export const buildBranchingChanges = (steps, path, selections, labels = {}) => {
    const onPath = new Set(path.map((step) => String(step.stepId)));
    const stepResponseData = inScreenOrder(steps).map((step) => {
        const selected = onPath.has(String(step.stepId)) ? selections[step.stepId] : undefined;
        const options = step.desiredValue.options ?? [];
        const label = labels[String(step.stepId)] || step.screen;
        return {
            stepId: step.stepId,
            stepKey: step.stepKey,
            stepResponseId: step.stepResponseId,
            screen: label,
            name: step.name,
            question: label.replace(/^\d+\.\s*/, ''),
            order: screenOrder(step),
            originalValue: branchingOriginalAnswer(step),
            originalInternalValue: step.originalValue,
            valueOptions: options,
            selectedValue: toLabel(selected, options),
            selectedInternalValue: isEmptyDcrValue(selected) ? '' : selected,
        };
    });
    return {
        dcr_intake__branching_logic_enabled: true,
        dcr_intake__step_response_data: stepResponseData,
    };
};
/** Whether a stored change set describes a branching activity rather than a single screen */
export const hasBranchingChanges = (changes) => changes.dcr_intake__branching_logic_enabled === true && Array.isArray(changes.dcr_intake__step_response_data);
export const getBranchingStepChanges = (changes) => hasBranchingChanges(changes) ? changes.dcr_intake__step_response_data : [];
/**
 * The answers a saved request already asks for, so editing it reopens the path the author built
 * rather than the answers on record.
 */
export const branchingSelectionsFromChanges = (changes) => getBranchingStepChanges(changes).reduce((selections, change) => {
    if (!isEmptyDcrValue(change.selectedInternalValue)) {
        selections[change.stepId] = change.selectedInternalValue;
    }
    return selections;
}, {});
